import React, { useRef, useState, useEffect, useContext } from "react";
import { useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { BrowserView, isBrowser } from "react-device-detect";
import { Box, Button } from "@mui/material";
import { getElementsDataApi, getMainModelDataApi, getBackgroundModelUrlApi } from "../../api/model";
import { getTasksDataApi } from "../../api/schedule";
import { getQTOIDApi } from "../../api/qto";
import { getCommentsApi } from "../../api/comment";
import { getCommentTypeApi } from "../../api/predefine.js";
import { getContractorsApi } from "../../api/member.js";
import { useUser } from "../../hook/useUser.js";
import { getElementStatus } from "../../utils/schedule.js";
import { rotateTo, zoomTo } from "../../component/bim/CameraControls";
import { NotificationContext } from "../../component/popup/NotificationProvider";
import dayjs from "dayjs";
import IFCViewer from "../../component/bim/IFCViewer.js";
import SelectComponent from "../../component/SelectComponent.js";
import ItemDetailsPopup from "./components/ItemDetailsPopup.js";
import ManageModelsPopup from "./components/ManageModelsPopup.js";
import FiltersPopup from "../../component/popup/FiltersPopup.js";

const ModelPage = () => {
  const { projectId } = useParams();
  const { showError } = useContext(NotificationContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useUser(projectId);
  const container = useRef();
  const [elementIds, setElementIds] = useState({
    planProgress: [],
    actualProgress: [],
    delayed: [],
    planComplete: [],
    actualComplete: [],
    actualHide: [],
    hideElementIds: [],
    showFilter: [],
    white: [],
    all: [],
  });
  const [state, setState] = useState({
    contractorOptions: [],
    contractorElementValue: [],
    workSetOptions: [],
    viewOptions: [
      { value: "Full view", label: "Full view" },
      { value: "Actual view", label: "Actual view" },
      { value: "Plan view", label: "Plan view" },
    ],
    backgroundOptions: [],
    worksetValue: [],
    viewValue: "",
    backgroundValue: [],
    backgroundModels: [],
    commentsFilterValue: "Off",
    commentsFilterOptions: [],
    expandedTasks: [],
    taskTableData: [],
    filteredTaskData: [],
    startDate: dayjs(),
    endDate: dayjs(),
    currentDate: dayjs(),
    radioValue: "Month",
    showPopup: false,
    modelURL: [],
    elementsData: [],
  });
  const [manageModelPopup, setManageModelPopup] = useState(false);
  const [comments, setComments] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [taskIdOptions, setTaskIdOptions] = useState([]);
  const [QTOIdOptions, setQTOIdOptions] = useState([]);
  const [selectedElement, setSelectedElement] = useState();
  const selectedElementRef = useRef(selectedElement); // for ifc viewer onclick event to reference latest selectedElement state
  const [commonProps, setCommonProps] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const showFiltersRef = useRef(false); // for headerRightButtonRef onclick event to reference latest showFilters state
  const { headerRightButtonRef } = useOutletContext();

  headerRightButtonRef.current = {
    icon: "assets/icons/tune.svg",
    onClick: () => {
      showFiltersRef.current = !showFiltersRef.current;
      setShowFilters(showFiltersRef.current);
    },
  };

  const selectComponents = [
    {
      Component: SelectComponent,
      options: state.workSetOptions,
      value: state.worksetValue,
      isMultiSelect: true,
      onChange: (value) => {
        setState((prevState) => ({
          ...prevState,
          worksetValue: value,
          modelURL: state.modelURL.map((mainModel) => {
            return {
              ...mainModel,
              visible: value.includes(mainModel.workset),
            };
          }),
        }));
      },
      selectLabel: "Workset",
      placeholder: "select an item...",
    },
    {
      Component: SelectComponent,
      options: state.viewOptions,
      value: state.viewValue,
      onChange: (value) => {
        setState((prevState) => ({
          ...prevState,
          viewValue: value,
        }));
      },
      selectLabel: "View",
    },
    {
      Component: SelectComponent,
      options: state.contractorOptions,
      value: state.contractorElementValue,
      isMultiSelect: true,
      onChange: (value) => {
        setState((prevState) => ({
          ...prevState,
          contractorElementValue: value,
        }));
      },
      selectLabel: "Contractor",
      placeholder: "select an item...",
    },
    {
      Component: SelectComponent,
      options: state.backgroundOptions,
      value: state.backgroundValue,
      isMultiSelect: true,
      required: false,
      onChange: (value) => {
        setState((prevState) => ({
          ...prevState,
          backgroundValue: value,
          backgroundModels: state.backgroundModels.map((backgroundModel) => {
            return {
              ...backgroundModel,
              visible: value.includes(backgroundModel.fileURL),
            };
          }),
        }));
      },
      selectLabel: "Background",
      placeholder: "Select an item...",
    },
    {
      Component: SelectComponent,
      options: state.commentsFilterOptions,
      value: state.commentsFilterValue,
      onChange: (value) => {
        setState((prevState) => ({
          ...prevState,
          commentsFilterValue: value,
        }));
      },
      selectLabel: "Comments",
    },
  ];

  const closeItemDetailsPopup = () => {
    setSelectedElement();
    container.current.selectionHighlight.clear();
  };

  //处理task data的children嵌套格式
  function flattenTaskData(data) {
    let result = [];

    function recurse(items) {
      if (Array.isArray(items)) {
        items.forEach((item) => {
          result.push(item);
          if (item.children && item.children.length > 0) {
            recurse(item.children);
          }
        });
      }
    }

    recurse(data);
    return result;
  }

  const handleElementClick = async (point, bimElementId, elementCenter) => {
    // no element clicked, reset
    if (!bimElementId) {
      setSelectedElement();
      container.current.selectionHighlight.clear();
      return;
    }
    // same element clicked, reset
    if (bimElementId === selectedElementRef.current?.bimElementId) {
      setSelectedElement();
      container.current.selectionHighlight.clear();
      return;
    }
    setSelectedElement({ point, bimElementId, elementCenter });
  };

  const closeManageModelPopup = () => {
    setManageModelPopup(false);
  };

  const colorRulesData = (data) => {
    const elementStatus = getElementStatus(data, state.currentDate);
    let allIds = data?.map((item) => item?.bimElementId);
    setElementIds((prevState) => ({
      ...prevState,
      ...elementStatus,
      all: allIds,
      // filter contractor
      hideElementIds:
        state.contractorElementValue.length === 0 || state.contractorElementValue.length === state.contractorOptions.length
          ? []
          : data.filter((item) => !state.contractorElementValue.includes(item.contractor)).map((item) => item?.bimElementId),
    }));
  };

  //get main model url and workset
  const getMainModel = async () => {
    const result = await getMainModelDataApi(projectId);
    const data = result?.data;

    if (data && Array.isArray(data)) {
      const mainModels = data.map((d) => {
        return {
          ...d,
          fileURL: d.fileURL,
          visible: true,
        };
      });

      setState((prevState) => ({
        ...prevState,
        modelURL: mainModels,
      }));

      const uniqueWorksets = new Set(data.map((item) => item.workset));
      const workSetOptionsArray = Array.from(uniqueWorksets).map((workset) => ({
        value: workset,
        label: workset,
      }));
      setState((prevState) => ({
        ...prevState,
        workSetOptions: workSetOptionsArray,
      }));

      if (uniqueWorksets.size > 0) {
        setState((prevState) => ({
          ...prevState,
          worksetValue: Array.from(uniqueWorksets),
        }));
      }
      await getElementsData();
    }
  };
  //get background model url&filter
  const getBackgroundModelUrl = async () => {
    const result = await getBackgroundModelUrlApi(projectId);
    const data = result?.data;
    if (data && Array.isArray(data)) {
      const backgroundModels = data.map((d) => {
        return {
          ...d,
          fileURL: d.fileURL,
          visible: false,
        };
      });
      const backgroundArray = backgroundModels.map((item) => ({
        value: item.fileURL,
        label: item.filename,
      }));
      setState((prevState) => ({
        ...prevState,
        backgroundOptions: backgroundArray,
        backgroundModels: backgroundModels,
        backgroundValue: [],
      }));
    }
  };

  const getElementsData = async () => {
    try {
      const result = await getElementsDataApi(projectId);
      const data = result?.data;
      if (!data) showError("No element data returned from API.");

      setState((prevState) => {
        const newState = {
          ...prevState,
          elementsData: data,
        };
        colorRulesData(newState.elementsData);
        return newState;
      });
    } catch {
      showError("Received element data occurred error.");
    }
  };

  //get task data
  const getTaskData = async () => {
    const result = await getTasksDataApi(projectId);
    const data = result?.data;
    if (!data || !Array.isArray(data)) {
      showError("Invalid or empty task data received.");
      return;
    }
    const flatData = flattenTaskData(data);
    setTasks(flatData);
    const validTasks = flattenTaskData(data).filter((task) => task.taskId && task.taskName && !task.children.length);
    const options = validTasks.map((task) => ({
      label: task.taskName,
      value: task.taskId,
    }));
    setTaskIdOptions(options);
  };

  //get qtoid option data
  const getQTOIDData = async () => {
    const result = await getQTOIDApi(projectId);
    const data = result?.data;
    if (!data || !Array.isArray(data)) {
      showError("Invalid or empty qto id data received.");
      return;
    }
    const options = data.map((item) => ({
      label: item.qtoId,
      value: item.qtoId,
    }));
    setQTOIdOptions(options);
  };

  //get all comments
  const getCommentsData = async () => {
    const result = await getCommentsApi(projectId, "element");
    if (result?.data) {
      setComments(result.data);
    }
  };

  //get comment type data
  const getCommentTypeData = async () => {
    try {
      const result = await getCommentTypeApi();
      setState((prevState) => ({
        ...prevState,
        commentsFilterOptions: [{ label: "Off", value: "Off" }, ...result?.data.map((d) => ({ label: d.label, value: d.label }))],
      }));
    } catch {
      showError("Received comment type data occurred error.");
    }
  };

  const getContractorsData = async () => {
    try {
      const result = await getContractorsApi(projectId);
      const data = result?.data;
      if (data) {
        const conTractors = new Set(data?.map((item) => item.name));
        const options = Array.from(conTractors).map((item) => ({
          label: item,
          value: item,
        }));
        setState((prevState) => ({
          ...prevState,
          contractorOptions: options,
        }));
        if (Array.from(conTractors).length > 0) {
          setState((prevState) => ({
            ...prevState,
            contractorElementValue: Array.from(conTractors),
          }));
        }
      }
    } catch {
      showError("Received contractor data occurred error.");
    }
  };

  const homeViewOnClick = () => {
    if (container.current) {
      zoomTo(container.current.world.camera, container.current.mainModels);
    }
  };

  const rotateToViewOnClick = (side) => {
    rotateTo(container.current.world.camera, side);
  };

  //manage schedule upload new version
  const handleFileUploadSuccess = () => {
    getMainModel();
    getBackgroundModelUrl();
    setState((prevState) => ({
      ...prevState,
      viewValue: "Actual view",
    }));
  };

  useEffect(() => {
    const fetchData = async () => {
      await Promise.all([
        getContractorsData(),
        getMainModel(),
        getBackgroundModelUrl(),
        getTaskData(),
        getQTOIDData(),
        getCommentsData(),
        getCommentTypeData(),
      ]);
      setState((prevState) => ({
        ...prevState,
        viewValue: "Actual view",
      }));
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (state.viewValue === "Actual view") {
      setCommonProps(() => ({
        showFilterElementIds: elementIds.showFilter,
        allElementIds: elementIds.all,
        highlightElementIds: [],
        hideElementIds: [...elementIds.actualHide, ...elementIds.hideElementIds],
        delayedNotStartElementIds: elementIds.delayedNotStart,
        inProgressElementIds: elementIds.actualProgress,
        delayedElementIds: elementIds.delayed,
      }));
    } else if (state.viewValue === "Plan view") {
      setCommonProps(() => ({
        showFilterElementIds: elementIds.showFilter,
        allElementIds: elementIds.all,
        highlightElementIds: [],
        hideElementIds: [...elementIds.planHide, ...elementIds.hideElementIds],
        inProgressElementIds: elementIds.planProgress,
      }));
    } else {
      setCommonProps(() => ({
        showFilterElementIds: elementIds.showFilter,
        allElementIds: elementIds.all,
        highlightElementIds: [],
        hideElementIds: elementIds.hideElementIds,
        delayedNotStartElementIds: [],
        inProgressElementIds: [],
        delayedElementIds: [],
      }));
    }
  }, [state.viewValue, elementIds]);

  useEffect(() => {
    if (state.worksetValue.length > 0 && state.elementsData.length > 0) {
      colorRulesData(state.elementsData);
    }
  }, [state.worksetValue, state.elementsData, state.currentDate, state.contractorElementValue]);

  useEffect(() => {
    selectedElementRef.current = selectedElement;
  }, [selectedElement]);

  return (
    <>
      <Box width="100%" height="100%" sx={{ position: "relative" }}>
        <BrowserView>
          <Button
            onClick={() => setManageModelPopup(true)}
            variant="contained"
            color="secondary"
            sx={{
              pr: 1.5,
              pl: 1,
              py: 0,
              lineHeight: "35px",
              borderRadius: "20px",
              zIndex: 99,
              position: "absolute",
              left: "20px",
              bottom: "20px",
            }}
            startIcon={<img src="assets/buttons/set.svg" alt="Manage Models" />}
          >
            Manage Models
          </Button>
        </BrowserView>
        <IFCViewer
          ref={container}
          modelURL={state.modelURL}
          backgroundModels={isBrowser ? state.backgroundModels : []}
          comments={
            state.commentsFilterValue === "Off" ? [] : comments.filter((c) => c.status === "Pending" && c.commentType === state.commentsFilterValue)
          }
          title={"3D Model View"}
          onClick={handleElementClick}
          selectComponents={isBrowser ? selectComponents : []}
          homeViewOnClick={homeViewOnClick}
          rotateToViewOnClick={rotateToViewOnClick}
          advanced
          defaultSelected={searchParams.get("selectedElementId")}
          selectionHighlightElementIds={selectedElement ? [selectedElement.bimElementId] : []}
          {...commonProps}
        ></IFCViewer>
      </Box>
      <ManageModelsPopup
        open={manageModelPopup}
        onClose={closeManageModelPopup}
        onUploadSuccess={handleFileUploadSuccess}
        user={user}
        qtoIds={QTOIdOptions.map((q) => q.label)}
      />
      <ItemDetailsPopup
        selectedElement={selectedElement}
        open={selectedElement}
        onClose={closeItemDetailsPopup}
        QTOIdOptions={QTOIdOptions}
        taskIdOptions={taskIdOptions}
        tasks={tasks}
        onCommentAdded={() => {
          getCommentsData();
        }}
        onCommentResolve={() => {
          getCommentsData();
        }}
        user={user}
      />
      <FiltersPopup open={showFilters} filters={selectComponents.filter((s) => s.selectLabel !== "Background")}></FiltersPopup>
    </>
  );
};

export default ModelPage;
